import Stepper from "@/components/common/Stepper";
import { AdminCreateUserWizardNav } from "@/features/admin/components/AdminCreateUserWizardNav";
import { useCreateUserWizardAccessState } from "@/features/admin/hooks/useCreateUserWizardAccessState";
import { useAccessCatalogueOptions } from "@/features/clinical-access/api/selectCatalogueOptions";
import { useSaveUserAccess } from "@/features/clinical-access/api/saveUserAccess";
import { UserAccessEditor } from "@/features/clinical-access/components/UserAccessEditor";
import type { MockUser, UserAccess } from "@/features/clinical-access/types";
import { getCreateUserWizardSteps, validateCreateUserWizardSearch } from "@/features/admin/utils/createUserWizard";
import { useProfileByIdQuerySafe } from "@/features/profile/api/selectProfile";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/_app/users/create-user/access")({
    validateSearch: validateCreateUserWizardSearch,
    beforeLoad: ({ search }) => {
        if (!search.userId) {
            throw redirect({ to: "/users/create-user/details" });
        }
    },
    component: CreateUserAccessStep,
});

function CreateUserAccessStep() {
    const search = Route.useSearch();
    const userId = search.userId!;
    const navigate = useNavigate();
    const { data: profile } = useProfileByIdQuerySafe(userId);
    const cataloguesQuery = useAccessCatalogueOptions();
    const saveUser = useSaveUserAccess();
    const { access, setAccess, baseline, setBaseline, overviewUser, isRemotePending, isHydrated } =
        useCreateUserWizardAccessState(userId);

    const displayName =
        [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") || profile?.email || "New user";

    const handleContinue = () => {
        // Templates are assigned on the dedicated wizard step - keep existing grants.
        const accessToSave: UserAccess = { ...access, templateIds: baseline.templateIds };
        const next: MockUser = {
            id: userId,
            name: displayName,
            email: profile?.email ?? "",
            role: "CLINICIAN",
            organisation: overviewUser?.organisation ?? "",
            access: accessToSave,
            hasCustomOptions: false,
            source: "admin",
        };

        saveUser.mutate(
            { next, previousAccess: baseline },
            {
                onSuccess: () => {
                    setBaseline(accessToSave);
                    void navigate({
                        to: "/users/create-user/templates",
                        search: { userId },
                    });
                },
            }
        );
    };

    return (
        <div className="space-y-6" data-testid="create-user-access-step">
            <Stepper steps={getCreateUserWizardSteps("access", search)} />
            <div className="space-y-2">
                <h2 className="font-medium text-lg">Assign access</h2>
                <p className="text-muted-foreground text-sm">
                    Choose catalogues, forms, labels, and schedules for {displayName}. Templates are next.
                </p>
            </div>

            {cataloguesQuery.isLoading || isRemotePending || !isHydrated ? (
                <div className="flex justify-center py-10">
                    <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
                </div>
            ) : cataloguesQuery.data ? (
                <UserAccessEditor
                    access={access}
                    catalogues={cataloguesQuery.data}
                    onChange={setAccess}
                    showTemplates={false}
                />
            ) : (
                <p className="text-destructive text-sm">Could not load access catalogues.</p>
            )}

            <AdminCreateUserWizardNav
                search={search}
                backTo="/users/create-user/organisations"
                nextLabel="Continue"
                nextLoading={saveUser.isPending}
                nextDisabled={!cataloguesQuery.data || !isHydrated || isRemotePending}
                onNextClick={handleContinue}
            />
        </div>
    );
}
